import {
  Collection,
  Entitlements,
  FeedResponse,
  Item,
  LayoutData,
  MediaResource,
  Profile,
  ResourceItem,
  ResourceType,
} from '#/client';
import { useConfig } from '#/hooks';
import { CoreFeatureConfig } from '#/service/config';
import { getDeviceId } from '#/service/user/userSessionManager';
import { useAuthentication } from '#/store/auth';
import { useFavorites } from '#/store/favorites';

/**
 * This function is mainly in reference to this confluence page: https://accedobroadband.jira.com/wiki/spaces/AccedoOne/pages/1377173549/Data+Binding+DRAFT
 * This function also covers the user of web specific contexts, but in general we should be refactoring this function to utilize the logic we have described in the above link.
 * @returns {object} Object with different context getter functions.
 */
export const useDataContext = () => {
  const {
    app: { serviceId, name, version = '' },
    theme,
    featureConfig,
  } = useConfig();
  const { isLoggedIn, isEntitled, profile, userId, entitlements } =
    useAuthentication();
  const { favoriteIds: favoriteItemIds } = useFavorites();

  const getGeneralContext = (): GeneralContext => ({
    app: {
      name: name,
      version: version,
    },
    user: {
      entitlements,
      isLoggedIn,
      isEntitled,
      favoriteItemIds,
      id: userId ?? '',
      profile,
    },
    featureConfig,
  });

  const getDetailsPageContext = (
    resourceType: string | undefined,
    resource: ResourceItem | Collection
  ): DetailsPageContext => ({
    item:
      resourceType === ResourceType.Item
        ? (resource as ResourceItem).data
        : (resource as Collection).collection,
    user: {
      entitlements,
      isLoggedIn,
      isEntitled,
      favoriteItemIds,
    },
    upNext:
      resourceType === ResourceType.Collection
        ? (resource as Collection).collection?.upNext
        : undefined,
  });

  const getPageContext = (defaultContext: object, layout: LayoutData) => ({
    serviceId: serviceId,
    // TODO:DYNUI - this shouldn't be needed in the context
    containerCollections: layout.containerCollections,
    ...defaultContext,
    // `user` should be under `defaultContext` to prevent it from being overwritten with the default logged out state.
    user: {
      entitlements,
      isLoggedIn,
      isEntitled,
      favoriteItemIds,
      profile,
    },
    theme,
    featureConfig,
  });

  const getSearchContext = (
    searchTerm?: string,
    searchResults?: FeedResponse
  ): SearchContext => {
    return {
      count: searchResults?.pageInfo?.total ?? 0,
      search: {
        query: searchTerm === '' ? undefined : searchTerm,
      },
      searchResults: {
        count: searchResults?.pageInfo?.total ?? 0,
        searchQuery: {
          text: searchTerm === '' ? undefined : searchTerm,
        },
      },
    };
  };

  const getWatchHistoryContext = (
    watchHistoryData: FeedResponse
  ): WatchHistoryContext => ({
    count: watchHistoryData.data.length,
  });

  const getFavoritesContext = (
    favoritesData: FeedResponse
  ): FavoritesContext => ({
    count: favoritesData.data.length,
  });

  const getSettingsContext = (): SettingsContext => ({
    app: {
      version,
    },
    platform: {
      browser: navigator.appVersion,
      uuid: getDeviceId(),
    },
    user: {
      // TODO: figure out how to align the strictly specified binding context schema to the arbitrary defined context schema
      ...profile?.attributes,
    },
  });

  return {
    getDetailsPageContext,
    getPageContext,
    getGeneralContext,
    getSearchContext,
    getSettingsContext,
    getWatchHistoryContext,
    getFavoritesContext,
  };
};

interface SettingsContext {
  app: {
    version: string;
  };
  platform: {
    browser: string;
    uuid: string;
  };
  user: any;
}

interface GeneralContext {
  app: {
    name: string;
    version: string;
  };
  user: {
    entitlements: Entitlements;
    isLoggedIn: boolean;
    isEntitled: boolean;
    favoriteItemIds: string[];
    id: string;
    profile: Profile;
  };
  featureConfig: CoreFeatureConfig;
}

interface SearchContext {
  count: number;
  search: {
    query?: string;
  };
  searchResults: {
    count: number;
    searchQuery: {
      text?: string;
    };
  };
}

interface DetailsPageContext {
  item: Item | MediaResource;
  user: {
    entitlements: Entitlements;
    isLoggedIn: boolean;
    isEntitled: boolean;
    favoriteItemIds: string[];
  };
  upNext?: Item;
}

interface WatchHistoryContext {
  count: number;
}

interface FavoritesContext {
  count: number;
}

export type GenericContext =
  | GeneralContext
  | SearchContext
  | DetailsPageContext
  | WatchHistoryContext
  | FavoritesContext;
